<template>
	<div class="transcription-bot-alert">
		<!-- bot has been deployed, but is in a retry loop -->
		<TeamingAlert
			v-if="isRetryingAlert"
			class="transcription-status-alert"
			type="SYSTEM"
			variant="HOLLOW"
		>
			<template #icon>
				<FontAwesomeIcon class="bot-icon" :icon="[ `fas`, `message-bot` ]" />
			</template>
			<template #default>
				<div class="alert-body">
					<p>
						{{ translate( `botRetryAlert`, {
							service: virtualMeetingServiceName
						} ) }}
					</p>
					<div>
						<TeamingButton2
							size="TINY"
							type="TERTIARY"
							@click="$emit( `cancelJoin`, { subDocId, } )"
						>{{ translate( `cancelButton` ) }}</TeamingButton2>
					</div>
				</div>
			</template>
		</TeamingAlert>

		<!-- bot has been deployed and is in some state -->
		<TeamingAlert
			v-else-if="isTranscribingAlert && !isRetryingAlert"
			class="transcription-status-alert"
			:type="botAlertConfiguration.alertType"
			:variant="botAlertConfiguration.alertVariant"
		>
			<template #icon v-if="botAlertConfiguration.icon">
				<FontAwesomeIcon class="bot-icon" :icon="botAlertConfiguration.icon" />
			</template>
			<template #default>
				<div class="alert-body">
					<p>
						{{ translate( botAlertConfiguration.translationKey, {
							service: virtualMeetingServiceName
						} ) }}
					</p>
					<div class="alert-body-buttons" v-if="botAlertConfiguration.cancelable || botAlertConfiguration.startable">
						<TeamingButton2
							v-if="botAlertConfiguration.cancelable"
							size="TINY"
							type="TERTIARY"
							@click="$emit( `cancelJoin`, { subDocId, } )"
						>{{ translate( `cancelButton` ) }}</TeamingButton2>
						<TeamingButton2
							v-if="botAlertConfiguration.startable"
							size="TINY"
							type="TERTIARY"
							@click="$emit( `forceJoin`, { subDocId, virtualMeetingUrl } )"
						>{{ translate( `startButton` ) }}</TeamingButton2>
					</div>
				</div>
			</template>
		</TeamingAlert>

		<div v-else><!-- intentionally empty --></div>
	</div>
</template>

<script>
import debugFactory from "debug";

import {
	EventsMixin,
	TranslateMixin,
	TeamingAlert,
	TeamingButton2
} from "@teaming/common-ui";

import {
	RecallAIBotStatuses as BotStatus,
	TranscriptionJobStatus,
	TranscriptionOrigin
} from "@teaming/common-enums";

import {
	resolveVirtualMeetingType
} from "../../domain";

const debug = debugFactory( "teaming:common-team:components:TranscriptionBotAlert" );

const botAlertConfigurations = {
	DEFAULT: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botIsJoiningAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
	},
	[ BotStatus.JOINING_CALL ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botIsJoiningAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: true,
		startable: false,
	},
	[ BotStatus.SCHEDULED ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botIsScheduledAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: true,
		startable: true,
	},
	[ BotStatus.IN_WAITING_ROOM ]: {
		alertType: "WARN",
		alertVariant: "HOLLOW",
		translationKey: "botIsWaitingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: true,
		startable: false,
	},
	[ BotStatus.IN_CALL_NOT_RECORDING ]: {
		alertType: "SUCCESS",
		alertVariant: "HOLLOW",
		translationKey: "botIsRecordingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
		//
		// TODO: enable once we figure out how to synchronize sub-status delivery better,
		// since IN_CALL_NOT_RECORDING and IN_CALL_RECORDING arrive almost simultaneously
		// and the status sometimes is recorded out-of-order...
		//
		// alertType: "WARN",
		// translationKey: "botIsNotRecordingAlert",
		// icon: [ "fas", "message-bot", ],
	},
	[ BotStatus.IN_CALL_RECORDING ]: {
		alertType: "SUCCESS",
		alertVariant: "HOLLOW",
		translationKey: "botIsRecordingAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
	},
	[ BotStatus.CALL_ENDED ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botCallEndedAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
	},
	[ BotStatus.DONE ]: {
		alertType: "SYSTEM",
		alertVariant: "HOLLOW",
		translationKey: "botCallEndedAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
	},
	[ BotStatus.FATAL ]: {
		alertType: "ERROR",
		alertVariant: "HOLLOW",
		translationKey: "botFatalAlert",
		icon: [ "fas", "message-bot", ],
		cancelable: false,
		startable: false,
	},
};

export default {
	name: "TranscriptionBotAlert",
	teamingPackage: "common-team",
	mixins: [ EventsMixin, TranslateMixin, ],
	emits: [ "cancelJoin", "forceJoin", ],
	components: {
		TeamingAlert,
		TeamingButton2,
	},
	props: {
		subDocId: {
			type: String,
			required: true,
		},
		virtualMeetingUrl: {
			type: String,
			required: true,
		},
		transcriptionOrigin: {
			type: String,
			required: true,
			validator: value => TranscriptionOrigin.includes( value ) || value === "",
		},
		transcriptionStatus: {
			type: String,
			required: true,
			validator: value => TranscriptionJobStatus.includes( value ) || value === "",
		},
		transcriptionSubStatus: {
			type: String,
			required: true,
			validator: value => BotStatus.includes( value ) || value === "",
		},
		// TODO: determine if needed
		// eslint-disable-next-line vue/no-unused-properties
		hasStatusUpdate: {
			type: Boolean,
			required: true,
		},
	},
	computed: {
		/** this is the source of truth for all computed values, and methods! */
		virtualMeetingType() {
			const { virtualMeetingUrl, } = this;
			return resolveVirtualMeetingType( virtualMeetingUrl );
		},
		virtualMeetingServiceName() {
			const { virtualMeetingType, } = this;
			if ( !virtualMeetingType ) {
				return "";
			}
			const translationKey = `${ virtualMeetingType }label`;
			return this.translate( translationKey );
		},
		/** alert configurations based on bot sub-statuses */
		botAlertConfiguration() {
			return botAlertConfigurations[ this.transcriptionSubStatus ] ||
				botAlertConfigurations.DEFAULT;
		},
		isTranscribingAlert() {
			return [
				TranscriptionJobStatus.REQUESTED,
				TranscriptionJobStatus.REQUESTED_RETRY,
				TranscriptionJobStatus.QUEUED,
				TranscriptionJobStatus.COMMITTED,
				TranscriptionJobStatus.SUMMARIZED,
			].includes( this.transcriptionStatus );
		},
		isRetryingAlert() {
			return this.transcriptionStatus === TranscriptionJobStatus.REQUESTED_RETRY;
		},
	},
	methods: {
		logSelf( lifecycleHook ) {
			const {
				subDocId,
				transcriptionOrigin,
				transcriptionStatus,
				transcriptionSubStatus,
			} = this;
			debug( [
				`[${ lifecycleHook }]`,
				`  subdoc ${ subDocId }`,
				`  origin ${ transcriptionOrigin }`,
				`  status ${ transcriptionStatus }`,
				`  subStatus ${ transcriptionSubStatus }`,
			].join( "\n" ) );
		},
	},
	mounted() {
		this.logSelf( "mounted" );
	},
	updated() {
		this.logSelf( "updated" );
	},
};
</script>

<style lang="scss" scoped>
$textColor: $color9-500;

.transcription-bot-alert {
	color: $textColor;
}

.transcription-bot-alert .alert-body {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 0.5rem;

	@include anchor() {
		color: $textColor !important;
	}
}

.transcription-bot-alert .alert-body-buttons {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	gap: 0.5rem;
}

.transcription-bot-alert .hint {
	@include font-and-line-for(-1);
}

.transcription-bot-alert .transient-url {
	width: 15.625rem; // 250px
}
</style>
